import PropTypes from 'prop-types';
import { Component } from 'react';
import ReactPaginate from 'react-paginate';
import { fetchArticles } from 'lib/AutolistAPI';
import {
  trackArticleSummaryPagination,
  trackArticleRssSubscription,
} from 'lib/Tracking';
import { useTranslation } from 'react-i18next';
import ArticleSummaries from 'components/ArticleSummaries';
// images
import arrowRight from 'public/static/images/arrow-right.png';
import arrowRightDisabled from 'public/static/images/arrow-right-disabled.png';
import arrowLeft from 'public/static/images/arrow-left.png';
import arrowLeftDisabled from 'public/static/images/arrow-left-disabled.png';
// styles
import ArticleSummariesPresenter from 'lib/Presenters/ArticleSummariesPresenter';
import { colors } from '../styles/variables';
import articlesCSS from '../styles/ContentDashboard/Articles';

export const DEFAULT_ARTICLE_SUMMARIES_COUNT = 6;

export async function fetchArticleSummaries(params = {}, options) {
  const defaultParams = {
    limit: DEFAULT_ARTICLE_SUMMARIES_COUNT,
    page: params.page || 1,
  };
  const combinedParams = Object.assign({}, defaultParams, params);
  const { articles, meta } = await fetchArticles(combinedParams, options);

  return {
    articleSummaries: articles,
    total: meta.total,
  };
}

export default class Articles extends Component {
  static propTypes = {
    parentCategory: PropTypes.string,
    rssUrl: PropTypes.string,
  };

  constructor(props) {
    super(props);
    const { articleSummaries, total } = props;
    this.state = { articleSummaries, total };
    this.fetch = this.fetch.bind(this);
  }

  async fetch(page) {
    const { parentCategory } = this.props;
    let params = { page };
    if (parentCategory) {
      params = Object.assign({}, params, { parentCategory });
    }
    const { articleSummaries, total } = await fetchArticleSummaries(params);

    this.setState({
      articleSummaries,
      total,
    });
  }

  get shouldShowPagination() {
    return this.state.total > DEFAULT_ARTICLE_SUMMARIES_COUNT;
  }

  get pageCount() {
    return Math.ceil(this.state.total / DEFAULT_ARTICLE_SUMMARIES_COUNT);
  }

  render() {
    const { title, rssUrl } = this.props;
    return (
      <section className="container" data-article-summaries>
        <a name="news" />
        <div className="row">
          <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12">
            <h2 className="articles-title">{title}</h2>
            <ArticleSummaries
              articleSummaries={ArticleSummariesPresenter.viewModelFrom(
                this.state.articleSummaries
              )}
            />
            <Pagination
              visible={this.shouldShowPagination}
              pageCount={this.pageCount}
              onPageChange={(page) => {
                trackArticleSummaryPagination({ page: page.selected + 1 });
                this.fetch(page.selected + 1);
              }}
            />
            <RSSLink rssUrl={rssUrl} />
          </div>
        </div>
        <style jsx>{articlesCSS}</style>
      </section>
    );
  }
}

Articles.propTypes = {
  articleSummaries: PropTypes.arrayOf(PropTypes.object).isRequired,
  total: PropTypes.number,
  title: PropTypes.string,
  rssUrl: PropTypes.string,
};

function RSSLink({ rssUrl }) {
  const { t } = useTranslation();

  return (
    <div className="rss-link">
      <span>{t('contentDashboard.rssLink.pre')}</span>
      <a href={rssUrl} onClick={trackArticleRssSubscription}>
        {t('contentDashboard.rssLink.link')}
      </a>
      <style jsx>
        {`
          .rss-link {
            text-align: center;
          }

          .rss-link span {
            font-size: 15px;
            margin-right: 5px;
          }
        `}
      </style>
    </div>
  );
}

RSSLink.propTypes = {
  rssUrl: PropTypes.string,
};

function Pagination({ visible, pageCount, onPageChange }) {
  const visibilityClass = visible ? '' : 'hidden';

  return (
    <div className={`AL-article-summary-pagination ${visibilityClass}`}>
      <ReactPaginate
        previousLabel={'Previous'}
        previousClassName={'prev'}
        nextLabel={'Next'}
        nextClassName={'next'}
        breakLabel={'...'}
        breakClassName={'break-me'}
        pageCount={pageCount}
        marginPagesDisplayed={2}
        pageRangeDisplayed={5} // pages are zero index, so add 1 for easier querying to api
        onPageChange={onPageChange}
        activeClassName={'active'}
      />
      <style global jsx>
        {`
          .AL-article-summary-pagination {
            text-align: center;
            margin: 40px 0 15px 0;
          }

          .AL-article-summary-pagination ul {
            margin: 0;
            padding: 0;
            list-style-type: none;
            text-align: center;
            position: relative;
            top: 4px;
            display: inline-block;
          }

          .AL-article-summary-pagination li {
            padding: 0 5px;
            margin: 0;
            display: inline-block;
            font-size: 14px;
            top: -5px;
          }

          .AL-article-summary-pagination a {
            position: relative;
            top: 0;
            color: ${colors.ALR1};
            text-decoration: none;

            -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
            -webkit-touch-callout: none;
            -webkit-appearance: none;
            user-select: none;
            outline: none;
          }

          .AL-article-summary-pagination a:focus,
          .AL-article-summary-pagination a:active,
          .AL-article-summary-pagination a:visited,
          .AL-article-summary-pagination a:hover {
            cursor: pointer;
            color: ${colors.ALR1};
            text-decoration: underline;
          }

          .AL-article-summary-pagination a:first-child,
          .AL-article-summary-pagination a:last-child {
            border: 0;
            width: auto;
          }

          .AL-article-summary-pagination a:first-child {
            margin-right: 7px;
          }

          .AL-article-summary-pagination a:last-child {
            margin-left: 7px;
          }

          .AL-article-summary-pagination li.active {
            padding: 0;
            border: 1px solid ${colors.G4};
            border-radius: 50px;
            height: 33px;
            width: 33px;
            top: -2px;
            position: relative;
            font-weight: 600;
            color: ${colors.G4};
            text-align: center;
            cursor: default;
          }

          .AL-article-summary-pagination li.active a {
            top: 2px;
            color: ${colors.G4};
            cursor: default;
          }

          .AL-article-summary-pagination .next a,
          .AL-article-summary-pagination .prev a {
            display: inline-block;
            background-size: 8px 15px;
            cursor: pointer;
          }

          .AL-article-summary-pagination .next a {
            background: url(${arrowRight}) no-repeat center right;
            padding-right: 20px;
          }

          .AL-article-summary-pagination .prev a {
            background: url(${arrowLeft}) no-repeat center left;
            padding-left: 20px;
          }

          .AL-article-summary-pagination .disabled a,
          .AL-article-summary-pagination .disabled a:hover,
          .AL-article-summary-pagination .disabled a:active,
          .AL-article-summary-pagination .disabled a:focus,
          .AL-article-summary-pagination .disabled a:visited {
            font-weight: 600;
            cursor: not-allowed;
            color: ${colors.G4};
            text-decoration: none;
          }

          .AL-article-summary-pagination .disabled.next a {
            background: url(${arrowRightDisabled}) no-repeat center right;
            cursor: not-allowed;
          }

          .AL-article-summary-pagination .disabled.prev a {
            background: url(${arrowLeftDisabled}) no-repeat center left;
            cursor: not-allowed;
          }
        `}
      </style>
    </div>
  );
}

Pagination.propTypes = {
  visible: PropTypes.bool.isRequired,
  pageCount: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
};
